// Presentation helpers for the Workbench. Pure functions only — no data fetching.

import type { WorkbenchFix, WorkbenchTier } from "@/types/workbench";

/** Tier display order (Technical validates → Authority proves → Brand secures). */
const TIER_ORDER: Record<WorkbenchTier, number> = { technical: 0, authority: 1, brand: 2 };

/** Derived lifecycle of a fix, reconciling the stored task status against the
 *  report the queue is built from.
 *
 *  A fix is only in the queue because the *latest* report still flags its signal
 *  (to_improve). So a task the user marked "done" splits in two:
 *   - "done"      — completed at/after this report ran: genuinely handled, just
 *                   waiting on the next monthly re-scan to clear it.
 *   - "regressed" — completed *before* this report ran, yet the report still
 *                   flags it: the fix didn't hold (or the signal slipped back),
 *                   so it's really open work again — we must not keep calling it
 *                   done. It rejoins the active queue with a "Rechecked" flag. */
export type FixLifecycle = "open" | "done" | "regressed";

export function fixLifecycle(fix: WorkbenchFix, reportCreatedAt?: string | null): FixLifecycle {
  if (fix.task.status !== "done") return "open";
  const completed = fix.task.completed_at;
  if (completed && reportCreatedAt) {
    const done = new Date(completed).getTime();
    const scan = new Date(reportCreatedAt).getTime();
    // Both timestamps valid and the report re-ran *after* completion → stale done.
    if (!Number.isNaN(done) && !Number.isNaN(scan) && done < scan) return "regressed";
  }
  return "done";
}

/** Whether a fix counts as outstanding work (open or regressed). Genuinely-done
 *  fixes awaiting the next scan don't. Single source of truth for the progress
 *  ring, the sidebar badge, and the queue grouping. */
export function isOutstanding(fix: WorkbenchFix, reportCreatedAt?: string | null): boolean {
  return fixLifecycle(fix, reportCreatedAt) !== "done";
}

/** Within an open bucket, critical above issue (preserving the backend's
 *  score-ranked order otherwise via the caller's stable sort). */
function severityRank(fix: WorkbenchFix): number {
  return fix.severity === "critical" ? 0 : 1;
}

export type QueueGroup = {
  key: string;
  label: string;
  fixes: WorkbenchFix[];
};

/** Group the fixes for the queue, by severity (default) or by tier. Outstanding
 *  fixes (open + regressed) are grouped by the chosen axis; genuinely-completed
 *  fixes are always pulled into a single trailing "Done" group (in both modes)
 *  so the UI can collapse them behind a "show completed" toggle. A regressed fix
 *  therefore rejoins its severity/tier bucket rather than hiding under "Done".
 *  The queue re-ranks as soon as a fix's lifecycle changes (the caller re-derives
 *  from the mutated cache). */
export function groupFixes(
  fixes: WorkbenchFix[],
  groupBy: "severity" | "tier",
  reportCreatedAt?: string | null,
): QueueGroup[] {
  const open = fixes.filter((f) => isOutstanding(f, reportCreatedAt));
  const done = fixes.filter((f) => !isOutstanding(f, reportCreatedAt));

  let groups: QueueGroup[];
  if (groupBy === "tier") {
    const byTier = new Map<string, WorkbenchFix[]>();
    [...open]
      .sort((a, b) => TIER_ORDER[a.tier] - TIER_ORDER[b.tier])
      .forEach((fix) => {
        const list = byTier.get(fix.tier) ?? [];
        list.push(fix);
        byTier.set(fix.tier, list);
      });
    groups = [...byTier.entries()].map(([tier, list]) => ({
      key: tier,
      label: list[0]?.tier_label ?? tier,
      // critical above issue within a tier
      fixes: [...list].sort((a, b) => severityRank(a) - severityRank(b)),
    }));
  } else {
    groups = [
      { key: "needs", label: "Needs attention", fixes: open.filter((f) => f.severity === "critical") },
      { key: "soon", label: "When you can", fixes: open.filter((f) => f.severity === "issue") },
    ].filter((g) => g.fixes.length > 0);
  }

  if (done.length) groups.push({ key: "done", label: "Done", fixes: done });
  return groups;
}

/** The next fix to auto-advance to after completing `currentKey`: the first still
 *  outstanding fix (open or regressed) that isn't the one just completed. Returns
 *  null when the queue is clear. */
export function nextOpenKey(
  fixes: WorkbenchFix[],
  currentKey: number | null,
  reportCreatedAt?: string | null,
): number | null {
  const open = fixes.filter(
    (f) => f.signal_key !== currentKey && isOutstanding(f, reportCreatedAt),
  );
  return open[0]?.signal_key ?? null;
}

/** "Jun 12" — for a task's completion date on the card + queue rows. */
export function formatShortDate(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** "June 2026" — for the report a fix was last scanned against. */
export function formatMonth(iso?: string | null): string {
  if (!iso) return "your latest";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "your latest"
    : d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}
